import { cn } from "../shared/ui/index.ts";
import { Interactions } from "../core/providers/interactions.provider.tsx";
import { I } from "../shared/icons/icons.tsx";
import { stripHtml } from "../shared/rich-text-editor.component.tsx";
import {
  clinicalCommentsForPatient,
  type ClinicalComment,
} from "./clinical-snippets.ts";
import {
  AdvancedGrid,
  type GridColumn,
} from "../shared/advanced-grid/advanced-grid.component.tsx";

const TYPE_LABEL: Record<ClinicalComment["type"], string> = {
  note: "Note",
  observation: "Observation",
  alert: "Alerte",
};

const TYPE_BADGE: Record<ClinicalComment["type"], string> = {
  note: "bg-blue-100 text-blue-700",
  observation: "bg-amber-100 text-amber-700",
  alert: "bg-red-100 text-red-700",
};

function isHtml(content: string): boolean {
  return /<[a-z][\s\S]*>/i.test(content);
}

function commentPreview(content: string): string {
  return isHtml(content) ? stripHtml(content) : content;
}

export function ClinicalCommentsGridWidget({ panelId }: { panelId: string }) {
  const { current } = Interactions.usePatient();

  if (!current) {
    return (
      <div className="flex h-full min-h-24 flex-col items-center justify-center rounded-xl border border-dashed border-sf-border py-8 text-center">
        <I.FileText size={20} className="mb-2 text-sf-text-muted" />
        <p className="text-[12.5px] text-sf-text-muted">
          Sélectionnez un patient
        </p>
      </div>
    );
  }

  const columns: GridColumn<ClinicalComment>[] = [
    {
      id: "type",
      label: "Type",
      accessor: (r) => r.type,
      sortable: true,
      width: "110px",
      render: (r) => (
        <span
          className={cn(
            "inline-block rounded px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide",
            TYPE_BADGE[r.type],
          )}
        >
          {TYPE_LABEL[r.type]}
        </span>
      ),
    },
    {
      id: "date",
      label: "Date",
      accessor: (r) => r.date,
      sortable: true,
      width: "110px",
      render: (r) => (
        <span className="tabular-nums text-sf-text-muted">
          {new Date(r.date).toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        </span>
      ),
    },
    {
      id: "author",
      label: "Auteur",
      accessor: (r) => r.author,
      sortable: true,
      width: "140px",
    },
    {
      id: "content",
      label: "Contenu",
      accessor: (r) => commentPreview(r.content),
      sortable: false,
      render: (r) => (
        <span className="text-sf-text">{commentPreview(r.content)}</span>
      ),
    },
  ];

  return (
    <AdvancedGrid<ClinicalComment>
      instanceId={panelId}
      columns={columns}
      rows={clinicalCommentsForPatient(current.uuid)}
      rowKey={(r) => r.id}
      emptyMessage="Aucun commentaire."
    />
  );
}
